import i18n from "../../../../i18n";

export const CHIP_DIMENSIONS = { LABOR: "labor", NON_LABOR: "non-labor", SPACE: "space", STOCK: "stock" };

const BADGE_ICON = { tier: "warning", stale: "clock" };
const LOADED_DIMENSIONS = new Set([CHIP_DIMENSIONS.LABOR, CHIP_DIMENSIONS.SPACE, CHIP_DIMENSIONS.STOCK]);

// The model is built for every rendered chip on every repaint: its fixed
// phrases are translated once per language, not per chip (an i18n call costs
// ~10 µs, which made the load colour the most expensive part of a chip).
const phrases = new Map();
const phrase = (k) => {
  const lang = i18n.language;
  const id = `${lang}|${k}`;
  let text = phrases.get(id);
  if (text === undefined) {
    text = i18n.t(k);
    phrases.set(id, text);
  }
  return text;
};

export const resourceChipModel = ({
  dimension, taskId, resourceId, name, acronym, valueText, detail, tone,
  compact = false, removable = false, editable = false, load = null,
}) => {
  const id = `${taskId}-${resourceId}`;
  // The load colours the acronym segment (the resource), never the value
  // segment, which only ever holds the value (units, cost, quantity): a red
  // "562%" beside the units read as the allocation rate. `text` is the peak
  // for screen readers and the tooltip; on screen the icon is the marker that
  // keeps the colour from carrying the meaning alone (A11Y-03).
  let badge = null;
  const classes = ["offolio-resource-chip", `offolio-resource-chip--${dimension}`];
  if (load?.state === "tier") {
    badge = { text: load.badgeText ?? i18n.t("resourceChip.badge.pct", { pct: Math.round(load.pct) }), icon: BADGE_ICON.tier, token: load.tierToken };
    classes.push(`offolio-resource-chip--load-${load.tierToken}`);
  } else if (load?.state === "stale") {
    badge = { text: null, icon: BADGE_ICON.stale, token: "stale" };
    classes.push("offolio-resource-chip--load-stale");
  }
  if (tone) classes.push(`offolio-resource-chip--tone-${tone}`);

  if (editable) classes.push("offolio-resource-chip--editable");
  // No value: the acronym segment alone, rounded on both ends.
  if (!valueText) classes.push("offolio-resource-chip--bare");
  // Compact = the look the cells had before SPEC-0022 (every option off): a
  // round Bryntum avatar with the initials, no value, no badge.
  const rootClass = compact
    ? ["offolio-resource-chip-compact", "b-resource-avatar", "b-resource-initials", tone && `b-sch-${tone}`].filter(Boolean).join(" ")
    : classes.join(" ");
  return {
    rootClass,
    compact,
    testId: `gantt-${dimension}-chip-${id}`,
    valueTestId: `gantt-${dimension}-chip-value-${id}`,
    badgeTestId: `gantt-${dimension}-chip-badge-${id}`,
    resourceId,
    dimension,
    acronym,
    valueText: compact ? "" : valueText,
    badge,
    // The chip is a labelled group; the loaded acronym is an image (a button
    // on labor), so the value (edit trigger) and the remove control stay
    // reachable.
    ariaLabel: [name, valueText].filter(Boolean).join(", "),
    // A loaded acronym opens the load popover (labor, Space, Stock: the
    // dimensions with a load, PDR-0104): a focusable button there.
    badgeInteractive: !!badge && LOADED_DIMENSIONS.has(dimension),
    badgeAriaLabel: badge
      ? [name, badge.text, phrase(`resourceChip.a11y.${badge.token}`), LOADED_DIMENSIONS.has(dimension) && phrase("resourceChip.a11y.openDetail")]
          .filter(Boolean)
          .join(", ")
      : null,
    tooltip: [name, valueText, detail, load?.tooltip].filter(Boolean).join(" · "),
    removable,
    editable,
  };
};
